import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { onSnapshot } from "firebase/firestore";
import { useAuth } from "../auth";
import { useI18n, type UiLang } from "../lib/i18n";
import type { DishDoc } from "../lib/types";

/** UI言語ごとのマニュアルのファイル。sop.html は render すれば必ず出る */
const FILES: Record<UiLang, string> = { ja: "sop.html", zh: "sop.zh.html" };

/** 品目に記録が無い場合の既定 */
const DEFAULT_LANGS = ["ja"];

/**
 * 生成物の HTML に組み込まれた言語リンク(🌐 日本語 | 中文)を隠す。
 * 画面の中では右上の言語切り替えに一本化する(切り替えが3か所あって迷う、
 * 2026-10-07 柴田の指示)。別タブで単体表示したときは HTML 側のリンクが残る。
 * /d/** は同一オリジンなので iframe の中へ書ける。
 */
function hideEmbeddedLangSwitch(frame: HTMLIFrameElement) {
  try {
    const doc = frame.contentDocument;
    if (!doc?.head || doc.getElementById("viewer-hide-lang")) return;
    const style = doc.createElement("style");
    style.id = "viewer-hide-lang";
    style.textContent = ".lang-switch{display:none !important}";
    doc.head.appendChild(style);
  } catch {
    /* 読めなくても本文は出る */
  }
}

/**
 * ③マニュアルの閲覧。
 *
 * 生成物は認可プロキシ(/d/{dishId}/**)越しに出す。iframe にしているのは、
 * sop.html が clips/*.mp4 を相対参照しており、同一オリジン・同一パス配下で
 * 解決させる必要があるため(設計 §4.1)。HTML を取り込んで描き直すと
 * リンクとクリップが壊れる。
 *
 * 表示する言語は画面の言語設定(右上の 日本語|中文)に従う。品目ごとの言語タブは
 * 置かない。中国語版が無い品目(docLangs に zh が無い)は日本語版を出し、その旨を添える。
 */
export function SopViewer() {
  const { dishId } = useParams<{ dishId: string }>();
  const { paths, profile } = useAuth();
  const { t, lang } = useI18n();
  const [failed, setFailed] = useState(false);
  const [langs, setLangs] = useState<string[]>(DEFAULT_LANGS);

  useEffect(() => {
    if (!dishId || !paths) return;
    return onSnapshot(paths.dishDoc(dishId), (s) => {
      const d = s.exists() ? (s.data() as DishDoc) : null;
      setLangs(d?.docLangs?.length ? d.docLangs : DEFAULT_LANGS);
    });
  }, [dishId, paths]);

  useEffect(() => setFailed(false), [lang]);

  if (!dishId) return null;
  const docLang: UiLang = langs.includes(lang) ? lang : "ja";
  const src = `/d/${dishId}/${FILES[docLang]}`;

  return (
    <div className="viewer">
      <div className="viewer-bar">
        <Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link>
        <span className="spacer" />
        {docLang !== lang && (
          <span className="muted small">{t("この品目の中国語版はまだありません。日本語版を表示しています。")}</span>
        )}
        {/* 作業者は管理者の同席中だけ見られる（PresenceGate）。別タブはこの画面を閉じても
            残るので出さない（閉じれば同席が終わる、という約束が崩れる） */}
        {profile?.role !== "worker" && (
          <a href={src} target="_blank" rel="noreferrer" className="link">
            {t("別タブで開く")}
          </a>
        )}
      </div>
      {failed ? (
        <div className="page">
          <p className="error">{t("この文書を表示できませんでした。")}</p>
          <p className="muted small">
            {t("まだ生成されていないか、閲覧権限がない可能性があります。権限の問題であればログインし直すと解消することがあります。")}
          </p>
        </div>
      ) : (
        <iframe
          key={src}
          className="viewer-frame"
          src={src}
          title={t("マニュアル")}
          onLoad={(e) => hideEmbeddedLangSwitch(e.currentTarget)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
